import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EventHero = ({ event, onRegister }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-[var(--color-success)] text-white';
      case 'ongoing':
        return 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)]';
      case 'completed':
        return 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]';
      default:
        return 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]';
    }
  };

  const calculateTimeRemaining = (eventDate) => {
    const now = new Date('2025-12-17T05:16:23.883Z');
    const event = new Date(eventDate);
    const diff = event - now;
    
    if (diff < 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return { days, hours };
  };

  const timeRemaining = calculateTimeRemaining(event?.date);

  return (
    <div className="relative bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <Image 
          src={event?.heroImage} 
          alt={event?.heroImageAlt}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${getStatusColor(event?.status)}`}>
                {event?.status?.charAt(0)?.toUpperCase() + event?.status?.slice(1)}
              </span>
              <span className="px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-sm font-medium">
                {event?.category}
              </span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              {event?.title}
            </h1>

            <p className="text-lg text-white/90 leading-relaxed">
              {event?.description}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon name="Calendar" size={24} />
                </div>
                <div>
                  <p className="text-sm text-white/70">Date</p>
                  <p className="font-semibold">{event?.dateFormatted}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon name="Clock" size={24} />
                </div>
                <div>
                  <p className="text-sm text-white/70">Time</p>
                  <p className="font-semibold">{event?.time}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon name="MapPin" size={24} />
                </div>
                <div>
                  <p className="text-sm text-white/70">Location</p>
                  <p className="font-semibold">{event?.location}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon name="Users" size={24} />
                </div>
                <div>
                  <p className="text-sm text-white/70">Volunteers</p>
                  <p className="font-semibold">{event?.registeredVolunteers} / {event?.maxVolunteers}</p>
                </div>
              </div>
            </div>

            {timeRemaining && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <p className="text-sm text-white/70 mb-2">Event starts in</p>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold">{timeRemaining?.days}</p>
                    <p className="text-sm text-white/70">Days</p>
                  </div>
                  <div className="text-3xl font-bold">:</div>
                  <div className="text-center">
                    <p className="text-4xl font-bold">{timeRemaining?.hours}</p>
                    <p className="text-sm text-white/70">Hours</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4 pt-4">
              <Button 
                variant="default" 
                size="lg"
                className="bg-white text-[var(--color-primary)] hover:bg-white/90"
                onClick={onRegister}
                disabled={event?.registeredVolunteers >= event?.maxVolunteers}
              >
                {event?.registeredVolunteers >= event?.maxVolunteers ? 'Event Full' : 'Register Now'}
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white/10"
                iconName="Share2"
                iconPosition="left"
              >
                Share Event
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <Image 
                src={event?.featuredImage} 
                alt={event?.featuredImageAlt}
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-6 shadow-xl max-w-xs">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Heart" size={20} color="var(--color-primary)" />
                <p className="font-semibold text-[var(--color-foreground)]">Impact Goal</p>
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)]">{event?.impactGoal}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventHero;